import { createContext, useContext } from 'react';

import { useLoadScript } from '@react-google-maps/api';

const libraries = ['places'];

/**
 * Consumers read map readiness from here instead of sniffing `window.google`.
 * The script loads asynchronously, so a render-time snapshot of the global has
 * no way to re-render once loading finishes.
 *
 * The default matches an unloaded API so components rendered outside the
 * provider (tests, isolated stories) fall back to their loading state.
 */
export const GoogleMapsContext = createContext({
  isLoaded: false,
  loadError: undefined,
});

export function useGoogleMapsStatus() {
  return useContext(GoogleMapsContext);
}

export default function useConfigureGoogleMaps() {
  // Loading the Google Maps script should only happen once, when the application is loaded.

  // `useLoadScript` (rather than `useJsApiLoader`) because only its URL
  // builder appends `loading=async`, the parameter Google's best-practice
  // loading guide asks for. `useJsApiLoader` delegates to
  // `@googlemaps/js-api-loader`, which omits it.
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY,
    // Load the `weekly` channel, which carries the Places API (New) surface
    // this app now uses (Autocomplete Data API, Place). It is also the loader
    // default; set explicitly to document the dependency (not a version pin).
    version: 'weekly',
    libraries,
  });

  if (loadError) {
    // eslint-disable-next-line no-console
    console.error(loadError);
  }

  return { isLoaded, loadError };
}
